<template>
  <div class="mt-4" id="mainPettyCashReport">
    <div class="bg-blue-50 border-2 border-blue-100 shadow-sm shadow-blue-100 p-4 rounded-md mb-3 flex flex-wrap items-end justify-between gap-4">
      <div class="flex flex-wrap items-end gap-4">
        <label class="block w-full md:w-auto">
          <span class="block mb-2 text-sm font-medium text-gray-800">Fec. de inicio</span>
          <input
            type="date"
            class="py-2 px-2 text-sm bg-gray-50 outline-none rounded-lg text-gray-600 w-full border-2 focus:border-blue-500 appearance-none"
            v-model="filters.dateStart"
            @keydown.enter.prevent
          />
        </label>
        <label class="block w-full md:w-auto">
          <span class="block mb-2 text-sm font-medium text-gray-800">Fec. término</span>
          <input
            type="date"
            class="py-2 px-2 text-sm bg-gray-50 outline-none rounded-lg text-gray-600 w-full border-2 focus:border-blue-500 appearance-none"
            v-model="filters.dateEnd"
            @keydown.enter.prevent
          />
        </label>
        <label class="block w-full md:w-auto">
          <span class="block mb-2 text-sm font-medium text-gray-800">Estado de caja</span>
          <select
            class="border-2 bg-gray-50 py-2 text-sm px-3 text-gray-600 cursor-pointer rounded-lg outline-none min-w-[160px] font-semibold w-full focus:border-blue-500 uppercase"
            v-model="filters.state"
          >
            <option value="">todos</option>
            <option value="0">abiertas</option>
            <option value="1">cerradas</option>
          </select>
        </label>
        <label class="block w-full md:w-auto md:min-w-[260px]">
          <span class="block mb-2 text-sm font-medium text-gray-800">Buscar</span>
          <input
            type="text"
            class="py-2 px-2 text-sm bg-gray-50 outline-none rounded-lg text-gray-600 w-full border-2 focus:border-blue-500"
            v-model="filters.search"
            placeholder="Responsable, beneficiario, referencia..."
            @keydown.enter.prevent="resetValuesAndGetData"
          />
        </label>
      </div>
      <button
        class="py-3 px-4 bg-blue-500 text-white text-sm rounded-md active:scale-95 w-full md:w-auto"
        @click="resetValuesAndGetData"
      >
        Buscar
      </button>
    </div>

    <div class="grid md:grid-cols-3 gap-4 mb-4">
      <div class="border rounded-lg p-4 bg-white shadow-sm">
        <div class="text-xs text-gray-500 font-semibold uppercase">Total aumentado</div>
        <div class="text-xl font-bold text-blue-600 mt-3">{{ money() }} {{ format(summary.total_aumentado || 0) }}</div>
      </div>
      <div class="border rounded-lg p-4 bg-white shadow-sm">
        <div class="text-xs text-gray-500 font-semibold uppercase">Total desembolsado</div>
        <div class="text-xl font-bold text-red-500 mt-3">{{ money() }} {{ format(summary.total_desembolsado || 0) }}</div>
      </div>
      <div class="border rounded-lg p-4 bg-white shadow-sm">
        <div class="text-xs text-gray-500 font-semibold uppercase">Movimientos</div>
        <div class="text-xl font-bold text-gray-700 mt-3">{{ summary.total_movimientos || 0 }}</div>
      </div>
      <div class="border rounded-lg p-4 bg-white shadow-sm">
        <div class="text-xs text-gray-500 font-semibold uppercase">Cajas afectadas</div>
        <div class="text-xl font-bold text-blue-600 mt-3">{{ summary.total_cajas || 0 }}</div>
      </div>
    </div>

    <div class="rounded-lg overflow-hidden border bg-white">
      <div class="px-3 py-4 flex flex-wrap items-center justify-between gap-4">
        <select
          v-model="limitRegister"
          class="bg-white border py-2 text-sm px-3 text-gray-600 cursor-pointer rounded-lg outline-none min-w-[140px] font-semibold"
          @change="resetValuesAndGetData"
        >
          <option value="10">10 Registros</option>
          <option value="25">25 Registros</option>
          <option value="50">50 Registros</option>
          <option value="100">100 Registros</option>
          <option value="500">500 Registros</option>
        </select>

        <div class="relative group">
          <button class="bg-blue-100 flex items-center justify-center w-28 h-10 rounded-md text-blue-600">
            Opciones
            <font-awesome-icon icon="fa-solid fa-ellipsis" class="rotate-90 ml-2" />
          </button>
          <ul class="absolute top-full right-0 bg-white rounded-md shadow-lg overflow-hidden transition-all duration-300 transform scale-0 group-hover:scale-100 z-50">
            <button
              class="px-6 py-2 text-orange-500 flex items-center hover:bg-orange-50 whitespace-nowrap w-full"
              @click="exportToPDF(registers, filters.dateStart, filters.dateEnd, summary)"
            >
              <font-awesome-icon icon="fa-solid fa-print" class="mr-2" />
              Exportar a PDF
            </button>
            <button
              class="px-6 py-2 text-green-500 flex items-center hover:bg-green-50 whitespace-nowrap w-full"
              @click="exportToExcel(registers, filters.dateStart, filters.dateEnd, summary)"
            >
              <font-awesome-icon icon="fa-solid fa-print" class="mr-2" />
              Exportar a excel
            </button>
          </ul>
        </div>
      </div>

      <div class="w-full resize-x resize-generate" ref="widthNav" @resize="handleResize">
        <div class="overflow-x-auto">
          <table class="table-auto w-full">
            <TableHead :headers="header" />
            <LoadingTables v-if="loading" :columns="8" />
            <tbody class="text-gray-500 text-left border-t-[1px]" v-else>
              <tr
                v-for="(register, index) in registers"
                :key="index"
                class="hover:bg-blue-500 transition-colors hover:text-white capitalize font-semibold odd:bg-white even:bg-slate-50"
                @click="selectRow($event)"
              >
                <td class="border-slate-200 text-sm px-6 py-3 whitespace-nowrap">#{{ register.idcaja_chica }}</td>
                <td class="border-slate-200 text-sm px-6 py-3 whitespace-nowrap uppercase">
                  {{ register.tipo_movimiento === 'AUMENTO' ? 'AUMENTO' : 'DESEMBOLSO' }}
                </td>
                <td class="border-slate-200 text-sm px-6 py-3 whitespace-nowrap uppercase">{{ register.nombres }}</td>
                <td class="border-slate-200 text-sm px-6 py-3 whitespace-nowrap uppercase">{{ register.numero_referencia || '---' }}</td>
                <td class="border-slate-200 text-sm px-6 py-3 whitespace-nowrap uppercase">{{ register.beneficiario || '---' }}</td>
                <td class="border-slate-200 text-sm px-6 py-3 whitespace-nowrap uppercase">{{ register.departamento || '---' }}</td>
                <td class="border-slate-200 text-sm px-6 py-3 whitespace-nowrap">{{ register.concepto }}</td>
                <td class="border-slate-200 text-sm px-6 py-3 whitespace-nowrap" :class="{
                  'text-red-500': register.tipo_movimiento === 'DESEMBOLSO',
                  'text-green-600': register.tipo_movimiento === 'AUMENTO',
                }">{{ money() }} {{ format(register.monto) }}</td>
                <td class="border-slate-200 text-sm px-6 py-3 whitespace-nowrap">{{ formatDateTime(register.fecha_de_desembolso) }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <TablePagination
        :currentPage="currentPage"
        :total="totalPage"
        :next="next"
        :prev="prev"
      />
    </div>
  </div>
</template>

<script>
import { onMounted, ref, watchEffect } from "vue";
import axios from "@/api";
import LoadingTables from "@/components/Loadings/LoadingTables";
import TableHead from "@/components/TablesComponents/TableHead";
import TablePagination from "@/components/TablesComponents/TablePagination.vue";
import { errorActions } from "@/alerts";
import { observeElement } from "@/observer";
import { idLocalStore } from "@/publicjs/localStorage";
import { money, format } from "@/publicjs/money";
import { formatDateTime } from "@/publicjs/formatDate";
import { selectRow } from "@/publicjs/selectRowTable";
import { exportToExcel } from "@/components/PettyCash/Reports/ExcelExportPettyCashReport";
import { exportToPDF } from "@/components/PettyCash/Reports/PDFExportPettyCashReport";

const getToday = () => new Date().toISOString().slice(0, 10);

export default {
  name: "MainPettyCashReport",
  components: {
    LoadingTables,
    TableHead,
    TablePagination,
  },
  setup() {
    const loading = ref(true);
    const registers = ref([]);
    const summary = ref({});
    const currentPage = ref(1);
    const limitRegister = ref(10);
    const totalPage = ref(0);
    const registerPaginate = ref(null);
    const idStore = ref(0);
    const widthNav = ref(null);
    const filters = ref({
      dateStart: getToday(),
      dateEnd: getToday(),
      state: "",
      search: "",
    });
    const header = [
      [
        { title: "caja" },
        { title: "tipo" },
        { title: "responsable" },
        { title: "referencia" },
        { title: "beneficiario" },
        { title: "departamento" },
        { title: "concepto" },
        { title: "monto" },
        { title: "fecha" },
      ],
    ];

    const handleResize = () => {
      const nav = document.querySelector("nav")?.offsetWidth || 0;
      watchEffect(() => {
        if (widthNav.value) {
          widthNav.value.style.width = `calc(100vw - ${nav + 42}px)`;
        }
      });
    };

    const getData = async () => {
      if (!filters.value.dateStart || !filters.value.dateEnd) {
        errorActions("Debes indicar el rango de fechas para generar el reporte");
        return;
      }

      loading.value = true;
      const response = await axios
        .get(
          `caja-chica/reporte?page=${currentPage.value}&per_page=${limitRegister.value}&idstore=${idStore.value}&search=${filters.value.search}&date_start=${filters.value.dateStart}&date_end=${filters.value.dateEnd}&state=${filters.value.state}`,
        )
        .catch((error) => errorActions(error));

      if (!response) {
        loading.value = false;
        return;
      }

      const data = await response.data;
      registers.value = data.registers.data;
      registerPaginate.value = data.registers;
      totalPage.value = data.registers.last_page;
      summary.value = data.summary || {};
      loading.value = false;
    };

    const resetValuesAndGetData = async () => {
      currentPage.value = 1;
      await getData();
    };

    const next = async () => {
      totalPage.value = registerPaginate.value.last_page;
      if (currentPage.value <= totalPage.value - 1) currentPage.value++;
      await getData();
    };

    const prev = async () => {
      totalPage.value = registerPaginate.value.last_page;
      if (currentPage.value >= 2) currentPage.value--;
      await getData();
    };

    onMounted(async () => {
      observeElement("#mainPettyCashReport");
      handleResize();
      window.addEventListener("resize", handleResize);
      idStore.value = await idLocalStore();
      await getData();
    });

    return {
      loading,
      registers,
      summary,
      currentPage,
      limitRegister,
      totalPage,
      filters,
      header,
      widthNav,
      handleResize,
      resetValuesAndGetData,
      next,
      prev,
      money,
      format,
      formatDateTime,
      selectRow,
      exportToExcel,
      exportToPDF,
    };
  },
};
</script>